import { Sparkles } from "lucide-react";
import { useSyncExternalStore, type ComponentType } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { Sheet, SheetBody, SheetContent, SheetHeader } from "@/components/ui/sheet";

/**
 * "Ask your business" side panel.
 *
 * The shell owns the drawer (so the button in the topbar and any feature can open
 * it from anywhere, including outside React events); the `ai` feature fills it in
 * by registering a renderer once, e.g. in its route module:
 *
 *   useEffect(() => registerAskAiPanel(AskAiConversation), []);
 *
 * Until then the drawer shows a branded placeholder.
 *
 * The same module owns a second slot: the AI DRAFT SHEET ("Draft payment
 * reminder", "Summarise this client", "Generate weekly report"). Any page may call
 * `openAiDraft({ type: "reminder", invoiceId })`; the `ai` feature registers the
 * sheet that renders it. See `ai-actions.tsx` for the ready-made buttons.
 */

/** A record the question is about (the record side panel, spec §9.1). */
export interface AskAiContext {
  entityType: "client" | "invoice" | "project" | "lead";
  entityId: string;
  /** What to call it in the panel, e.g. "Acme Retail". */
  label?: string | null;
}

export interface AskAiPanelProps {
  /** Seeded question, e.g. from the ⌘K palette. */
  prompt: string | null;
  /** Pinned record, when the panel was opened from one. */
  context: AskAiContext | null;
  /** Increments on every `openAskAi()` call, so the same prompt twice is two requests. */
  requestId: number;
  onClose: () => void;
}

type Renderer = ComponentType<AskAiPanelProps>;

interface AskAiState {
  open: boolean;
  prompt: string | null;
  context: AskAiContext | null;
  requestId: number;
  renderer: Renderer | null;
}

let state: AskAiState = { open: false, prompt: null, context: null, requestId: 0, renderer: null };
const listeners = new Set<() => void>();

function emit(next: AskAiState): void {
  state = next;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): AskAiState {
  return state;
}

/** Opens the panel, optionally seeded with a question and/or pinned to a record. Safe to call anywhere. */
export function openAskAi(prompt?: string, context?: AskAiContext): void {
  emit({ ...state, open: true, prompt: prompt ?? null, context: context ?? null, requestId: state.requestId + 1 });
}

export function closeAskAi(): void {
  emit({ ...state, open: false, prompt: null });
}

/** ⌘J: open the panel, or close it when it is already open. */
export function toggleAskAi(): void {
  if (state.open) closeAskAi();
  else openAskAi();
}

/** Registers the real panel. Returns an unregister function (use it in cleanup). */
export function registerAskAiPanel(renderer: Renderer | null): () => void {
  emit({ ...state, renderer });
  return () => {
    if (state.renderer === renderer) emit({ ...state, renderer: null });
  };
}

export function useAskAi(): AskAiState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ---------------------------------------------------------------------------
// The AI draft sheet slot
// ---------------------------------------------------------------------------

/** What a page asks the assistant to draft (or which existing draft to open). */
export type AiDraftRequest =
  | { type: "reminder"; invoiceId: string; label?: string | null }
  | { type: "client_reminder"; clientId: string; label?: string | null }
  | { type: "summary"; clientId: string; label?: string | null }
  | { type: "weekly_report"; clientId: string; weekOf?: string; label?: string | null }
  | { type: "open"; draftId: string; label?: string | null };

export interface AiDraftSheetProps {
  request: AiDraftRequest;
  /** Increments on every `openAiDraft()` call. */
  requestId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DraftRenderer = ComponentType<AiDraftSheetProps>;

interface AiDraftState {
  open: boolean;
  request: AiDraftRequest | null;
  requestId: number;
  renderer: DraftRenderer | null;
}

let draftState: AiDraftState = { open: false, request: null, requestId: 0, renderer: null };
const draftListeners = new Set<() => void>();

function emitDraft(next: AiDraftState): void {
  draftState = next;
  for (const l of draftListeners) l();
}

function subscribeDraft(listener: () => void): () => void {
  draftListeners.add(listener);
  return () => {
    draftListeners.delete(listener);
  };
}

function getDraftSnapshot(): AiDraftState {
  return draftState;
}

/** Opens the AI draft sheet for a record. The `ai` feature decides what to render. */
export function openAiDraft(request: AiDraftRequest): void {
  emitDraft({ ...draftState, open: true, request, requestId: draftState.requestId + 1 });
}

/** Closes the sheet but keeps the request, so the closing animation still has content. */
export function closeAiDraft(): void {
  emitDraft({ ...draftState, open: false });
}

export function registerAiDraftSheet(renderer: DraftRenderer | null): () => void {
  emitDraft({ ...draftState, renderer });
  return () => {
    if (draftState.renderer === renderer) emitDraft({ ...draftState, renderer: null });
  };
}

export function useAiDraftSlot(): AiDraftState {
  return useSyncExternalStore(subscribeDraft, getDraftSnapshot, getDraftSnapshot);
}

function AiDraftHost() {
  const { open, request, requestId, renderer: Renderer } = useAiDraftSlot();
  if (!Renderer || !request) return null;
  return <Renderer request={request} requestId={requestId} open={open} onOpenChange={(o) => (o ? emitDraft({ ...draftState, open: true }) : closeAiDraft())} />;
}

export function AskAiPanel() {
  const { open, prompt, context, requestId, renderer: Renderer } = useAskAi();
  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && closeAskAi()}>
        <SheetContent size="lg" aria-label="Ask your business">
          {Renderer ? (
            <Renderer prompt={prompt} context={context} requestId={requestId} onClose={closeAskAi} />
          ) : (
            <>
              <SheetHeader eyebrow="Intelligence" title="Ask your business" description="Questions about your own workspace, answered from your own records." />
              <SheetBody>
                <EmptyState
                  icon={<Sparkles strokeWidth={1.75} />}
                  title="Almost ready"
                  description={
                    prompt
                      ? `“${prompt}” will be answered here once the assistant is switched on for your workspace.`
                      : "Ask “which clients slipped this month?” or “what is unbilled on Project Meridian?” and get an answer that cites the records it used."
                  }
                />
              </SheetBody>
            </>
          )}
        </SheetContent>
      </Sheet>
      <AiDraftHost />
    </>
  );
}
